import * as Sentry from "@sentry/react";
import Auth from "src/Auth.js";
import { feedbackSuggestionLooksLikeEditorialAdvice } from "~shared/feedbackSuggestionValidation";
import {
  applySuggestionToEditor,
  applySuggestionToPassageInEditor,
  feedbackOriginalFallbackTargetIsSafe,
  getAnnotationRangeInEditor,
} from "src/utils/tiptapSuggestion";

function getChangedFeedbackTarget(tiptap, comment) {
  const { annotationId, original, suggestion } = comment;
  const originalText = typeof original === "string" ? original.trim() : "";
  if (!originalText) return null;

  const originalRange = getAnnotationRangeInEditor(tiptap, annotationId, {
    preferredText: originalText,
  });
  if (originalRange) return null;

  // Only offer an override when all live marks form one contiguous range. An
  // ambiguous/split annotation remains blocked so the dialog cannot turn the
  // escape hatch into another wrong-range replacement path.
  const currentRange = getAnnotationRangeInEditor(tiptap, annotationId);
  const currentText = currentRange?.text?.trim();
  if (!currentText) return null;

  return {
    originalText,
    currentText,
    suggestionText: suggestion,
  };
}

function applySuggestionToFeedbackTarget(tiptap, comment, confirmedTargetText) {
  const { suggestion, annotationId, original } = comment;
  const originalPassage = typeof original === "string" ? original.trim() : "";
  const preferredText = confirmedTargetText || originalPassage || undefined;
  if (applySuggestionToEditor(tiptap, suggestion, {
    annotationId,
    skipInsertionMark: true,
    // When the same annotation id is split across separated spans, prefer the
    // group that still matches the stored original instead of failing closed.
    preferredText,
    validateTargetBoundary: true,
  })) {
    return true;
  }

  // A confirmed override is valid only for the exact live text shown in the
  // dialog. If it changed while the dialog was open, do not fall back to a
  // different occurrence of the stored original elsewhere in the manuscript.
  if (confirmedTargetText) {
    return false;
  }

  if (!originalPassage) {
    return false;
  }
  if (!feedbackOriginalFallbackTargetIsSafe(tiptap, originalPassage)) {
    return false;
  }

  return applySuggestionToPassageInEditor(tiptap, suggestion, {
    passage: originalPassage,
    skipInsertionMark: true,
    validateTargetBoundary: true,
  });
}

export async function applyFeedbackComment(
  comment,
  {
    tiptap,
    fetchWithIdToken = Auth.fetchWithIdToken,
    resolveComment,
    withSuppressedMarkRemoval = (_annotationId, fn) => fn(),
    confirmChangedTarget,
    notifyInvalidSuggestion,
    captureException = Sentry.captureException,
  },
) {
  if (!tiptap) return false;
  const { id, suggestion, annotationId } = comment;
  try {
    const hasSuggestion = typeof suggestion === "string" && suggestion.trim() !== "";
    if (!hasSuggestion) {
      fetchWithIdToken(`/api/comments/${id}/accept`);
      resolveComment(annotationId);
      return true;
    }

    if (feedbackSuggestionLooksLikeEditorialAdvice(suggestion)) {
      notifyInvalidSuggestion?.();
      return false;
    }

    const changedTarget = getChangedFeedbackTarget(tiptap, comment);
    let confirmedTargetText;
    if (changedTarget) {
      if (typeof confirmChangedTarget !== "function") return false;
      const confirmed = await confirmChangedTarget(changedTarget);
      if (!confirmed) return false;
      confirmedTargetText = changedTarget.currentText;
    }

    // Apply unsets the annotation mark before accept/resolve. Suppress orphan-delete
    // onMarkRemoved handling so successful Apply cannot race-delete the card.
    const applied = withSuppressedMarkRemoval(annotationId, () => (
      applySuggestionToFeedbackTarget(tiptap, comment, confirmedTargetText)
    ));
    if (!applied) {
      return false;
    }

    fetchWithIdToken(`/api/comments/${id}/accept`);
    resolveComment(annotationId);
    return true;
  } catch (error) {
    captureException(error, { tags: { func: "handleApplyFeedback" } });
    return false;
  }
}
